import type { ReactNode } from 'react'
import { MENTION_RE } from '@/lib/comment-mentions'
import { cn } from '@/lib/utils'

/**
 * Render a plain-text comment body into React nodes, turning inline mention
 * tokens (`@[Full Name](user:<uuid>)`) into chip `<span>`s showing `@Name`.
 *
 * Plain-text segments are returned as strings; each token becomes a rounded
 * `bg-muted` pill. When the token's `user:id` matches `currentUserId`, the chip
 * is tinted iris (the "your own mention" highlight).
 */
export function renderMentionsToNodes(body: string, currentUserId?: string): ReactNode {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let key = 0

  // MENTION_RE is a global regex — reset lastIndex so repeated calls are safe.
  MENTION_RE.lastIndex = 0
  for (const match of body.matchAll(MENTION_RE)) {
    const [token, name, userId] = match
    const start = match.index ?? 0

    if (start > lastIndex) {
      nodes.push(body.slice(lastIndex, start))
    }

    const isSelf = !!currentUserId && userId === currentUserId
    nodes.push(
      <span
        key={key++}
        className={cn(
          'rounded px-1 font-medium bg-muted',
          isSelf && 'bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]',
        )}
      >
        @{name}
      </span>,
    )

    lastIndex = start + token.length
  }

  if (lastIndex < body.length) {
    nodes.push(body.slice(lastIndex))
  }

  return nodes
}
